import React, { useState, useEffect } from 'react';
import { FaSave, FaTrash, FaPlus, FaInstagram, FaDiscord, FaTwitter, FaTwitch, FaYoutube, FaTiktok, FaSteam, FaSignOutAlt} from 'react-icons/fa';
import LoginPage from './LoginPage';
import { useAuth } from '../contexts/AuthContext';
import { ConfigSection, AdvancedVideoSetting } from '../hooks/useContent';
import { useRefresh } from '../context/RefreshContext';

interface SocialLink {
  id: string;
  label: string;
  link: string;
  icon: string;
}

interface SystemSpec {
  id: string;
  label: string;
  value: string;
}

interface CS2Setting {
  id: string;
  label: string;
  value: string;
}

interface ViewModel {
  id: string;
  label: string;
  value: string;
}

interface VideoSetting {
  id: string;
  label: string;
  value: string;
}

interface Bind {
  id: string;
  label: string;
  key: string;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

interface Reward {
  id: string;
  title: string;
  imageUrl: string;
  link: string;
}

interface Sponsor {
  id: string;
  label: string;
  link: string;
}

const AdminPage: React.FC = () => {
  const { sessionId, isAuthenticated, login, logout } = useAuth();
  const { triggerRefresh } = useRefresh();
  const [activeTab, setActiveTab] = useState('social');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Social Media Links
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([
    { id: '1', label: 'Faceit', link: 'https://www.faceit.com/de/players/mika666', icon: 'faceit' },
    { id: '2', label: 'Steam', link: 'https://steamcommunity.com/profiles/76561198088755702', icon: 'steam' },
    { id: '3', label: 'Instagram', link: 'https://www.instagram.com/150kbtw/', icon: 'instagram' },
    { id: '4', label: 'Discord', link: 'https://discord.gg/CwX4TQxszq', icon: 'discord' },
    { id: '5', label: 'Twitter', link: 'https://x.com/150kbtw', icon: 'twitter' },
    { id: '6', label: 'Twitch', link: 'https://www.twitch.tv/150k_btw', icon: 'twitch' },
    { id: '7', label: 'Youtube', link: 'https://www.youtube.com/@150kbtw', icon: 'youtube' },
    { id: '8', label: 'Tiktok', link: 'https://www.tiktok.com/@150kbtw', icon: 'tiktok' },
  ]);

  const [systemSpecs, setSystemSpecs] = useState<SystemSpec[]>([
    { id: '1', label: 'CPU', value: 'Amd Ryzen 9 5900x' },
    { id: '2', label: 'GPU', value: 'Asus Geforce TUF RTX 3080 O10G' },
    { id: '3', label: 'RAM', value: 'Dominator Platinum 32GB' },
    { id: '4', label: 'MOTHERBOARD', value: 'Asus TUF gaming b550+' },
    { id: '5', label: 'COOLER', value: 'Kraken X63' },
    { id: '6', label: 'SSD', value: 'Samsung 980 Pro 512GB' },
    { id: '7', label: 'MOUSE', value: 'Logitech G PRO X Superlight 2' },
    { id: '8', label: 'MOUSEPAD', value: 'Zowie Gear G-SR-SE' },
    { id: '9', label: 'MONITOR', value: 'BenQ Zowie 240 Hz' },
    { id: '10', label: 'KEYBOARD', value: 'Wooting 60HE+' },
    { id: '11', label: 'HEADPHONES', value: 'Steelseries Arctis Nova Pro Wireless' },
  ]);

  const [cs2Settings, setCs2Settings] = useState<CS2Setting[]>([
    { id: '1', label: 'DPI', value: '800' },
    { id: '2', label: 'Sensitivity', value: '0.94' },
    { id: '3', label: 'eDPI', value: '752' },
    { id: '4', label: 'Zoom Sensitivity', value: '1' },
    { id: '5', label: 'Windows Sensitivity', value: '6' },
    { id: '6', label: 'Hz', value: '4000' },
  ]);

  const [viewmodel, setViewmodel] = useState<ViewModel[]>([
    { id: '1', label: 'FOV', value: '68' },
    { id: '2', label: 'Offset X', value: '-2' },
    { id: '3', label: 'Offset Y', value: '2' },
    { id: '4', label: 'Offset Z', value: '-2' },
    { id: '5', label: 'Presets', value: '0' },
  ]);

  const [videoSettings, setVideoSettings] = useState<VideoSetting[]>([
    { id: '1', label: 'Resolution', value: '1280x1024' },
    { id: '2', label: 'Aspect Ratio', value: '4:3' },
    { id: '3', label: 'Scaling Mode', value: 'STRETCHED' },
    { id: '4', label: 'Brightness', value: '80%' },
    { id: '5', label: 'Display Mode', value: 'FULLSCREEN' },
  ]);

  const [binds, setBinds] = useState<Bind[]>([
    { id: '1', label: 'Toggle Inventory Display', key: 'I' },
    { id: '2', label: 'Move Forward', key: 'W' },
    { id: '3', label: 'Move Backward', key: 'S' },
    { id: '4', label: 'Move Left (strafe)', key: 'A' },
    { id: '5', label: 'Move Right (strafe)', key: 'D' },
    { id: '6', label: 'Walk', key: 'CAPSLOCK' },
    { id: '7', label: 'Duck', key: 'SHIFT' },
    { id: '8', label: 'Jump', key: 'SPACE' },
    { id: '9', label: 'Use', key: 'E' },
    { id: '10', label: 'Fire', key: 'MOUSE1' },
    { id: '11', label: 'Secondary Fire', key: 'MOUSE2' },
    { id: '12', label: 'Reload', key: 'R' },
    { id: '13', label: 'Radial Weapon Menu', key: 'nan' },
    { id: '14', label: 'Select Previous Weapon', key: 'MWHEELUP' },
    { id: '15', label: 'Select Next Weapon', key: 'MWHEELDOWN' },
    { id: '16', label: 'Last Weapon Used', key: 'Q' },
    { id: '17', label: 'Drop Weapon', key: 'G' },
    { id: '18', label: 'Inspect Weapon', key: 'F' },
    { id: '19', label: 'Switch Viewmodel Left/Right Hand', key: 'nan' },
    { id: '20', label: 'Buy Menu', key: 'B' },
    { id: '21', label: 'Autobuy', key: 'F3' },
    { id: '22', label: 'Rebuy', key: 'F4' },
    { id: '23', label: 'Primary Weapon', key: '1' },
    { id: '24', label: 'Secondary Weapon', key: '2' },
    { id: '25', label: 'Melee Weapons', key: '3' },
    { id: '26', label: 'Cycle Grenades', key: '4' },
    { id: '27', label: 'Explosives & Traps', key: '5' },
    { id: '28', label: 'HE Grenade', key: '6' },
    { id: '29', label: 'Flashbang', key: '7' },
    { id: '30', label: 'Smoke Grenade', key: '8' },
    { id: '31', label: 'Decoy Grenade', key: '9' },
    { id: '32', label: 'Molotov Cocktail / Incendiary Grenade', key: '0' },
    { id: '33', label: 'Zeus x27', key: 'X' },
    { id: '34', label: 'Medi-Shot', key: 'nan' },
    { id: '35', label: 'Utility Items', key: 'nan' },
    { id: '36', label: 'Graffiti Menu', key: 'T' },
    { id: '37', label: 'Scoreboard', key: 'TAB' },
    { id: '38', label: 'Show Team Equipment', key: 'nan' },
    { id: '39', label: 'Toggle Radar Zoom', key: 'V' },
    { id: '40', label: 'Call Vote', key: 'nan' },
    { id: '41', label: 'Choose Team', key: 'M' },
    { id: '42', label: 'Toggle Console', key: 'J' },
    { id: '43', label: 'Player Ping', key: 'nan' },
    { id: '44', label: 'Radio Message', key: 'nan' },
    { id: '45', label: 'Command Radio Message', key: 'nan' },
    { id: '46', label: 'Standard Radio Message', key: 'nan' },
    { id: '47', label: 'Report Radio Message', key: 'nan' },
    { id: '48', label: 'Team Message', key: 'U' },
    { id: '49', label: 'Chat Message', key: 'Z' },
    { id: '50', label: 'Use Microphone', key: 'MOUSE5' },
    { id: '51', label: 'Temporarily Disable Incoming Chat', key: 'nan' },
    { id: '52', label: 'Chat Wheel 1', key: 'nan' },
    { id: '53', label: 'Chat Wheel 2', key: 'nan' },
    { id: '54', label: 'Chat Wheel 3', key: 'nan' },
  ]);

  const [faqs, setFaqs] = useState<FAQ[]>([
    {
      id: '1',
      question: "How did you come up with the name 150k_btw?",
      answer: "Back in the day, when I was grinding Call of Duty, having a good internet connection gave you a real advantage. I had the best connection around—and because of that, people started calling me \"Connecto\". I wasn't a fan of the name, though, so I went with something else: \"150k\"—which was actually the speed of my connection back then. And that's how the name was born. EZ as that."
    },
    {
      id: '2',
      question: "Whats your name? Whats your Age? Whats your Height? Where are you from?",
      answer: "Mika 25 193cm Switzerland"
    },
    {
      id: '3',
      question: "How do you have VAC and Game Bans",
      answer: "MW3 Modded Lobbies / COD Ghost Levelhack (10 Years ago)"
    },
    {
      id: '4',
      question: "How do I join the community?",
      answer: "Click the Discord or Steam icon on Homepage to join our community and get started!"
    },
    {
      id: '5',
      question: "Where can I find your config?",
      answer: "The config is available on the settings page. Click the Config button to download."
    }
  ]);

  const [configSection, setConfigSection] = useState<ConfigSection>({
    configLink: 'https://drive.google.com/file/d/1wbpFIlQKe4vsAW0F36W1h4jG4oEZMoJ4/view?usp=sharing',
    launchOptions: 'Launch Options',
    crosshair: 'CSGO-Dadg-j6hfr-pH5eO-4onNB-VAmJ',
    viewmodel: 'viewmodel_fov 68;viewmodel_offset_x -2;viewmodel_offset_y 2;viewmodel_offset_z -2;cl_prefer_lefthanded true',
    hud: 'cl_hud_color 10;cl_showloadout true;safezone 1;safezoneY 1',
    radar: 'cl_hud_radar_scale 1.09962;cl_radar_scale 1;cl_radar_always_centered true;cl_radar_rotate true;cl_radar_icon_scale_min 0.6'
  });

  const [mouseSettings, setMouseSettings] = useState<SystemSpec[]>([
    { id: '1', label: 'DPI', value: '800' },
    { id: '2', label: 'Mouse Sensitivity', value: '0.94' },
    { id: '3', label: 'Zoom Sensitivity Multiplier', value: '1.04' },
    { id: '4', label: 'Polling rate', value: '500 Hz' },
    { id: '5', label: 'm_yaw', value: '0.022' },
  ]);

  const [advancedVideoSettings, setAdvancedVideoSettings] = useState<AdvancedVideoSetting[]>([
    { id: '1', label: 'Boost Player Contrast', value: 'Enabled', status: 'enabled' },
    { id: '2', label: 'V-Sync', value: 'Disabled', status: 'disabled' },
    { id: '3', label: 'Multisampling Anti-Aliasing Mode', value: 'None' },
    { id: '4', label: 'Global Shadow Quality', value: 'Low' },
    { id: '5', label: 'Model / Texture Detail', value: 'Low' },
    { id: '6', label: 'Texture Filtering Mode', value: 'Bilinear' },
    { id: '7', label: 'Shader Detail', value: 'Low' },
    { id: '8', label: 'Particle Detail', value: 'Low' },
    { id: '9', label: 'Ambient Occlusion', value: 'Disabled' },
    { id: '10', label: 'High Dynamic Range', value: 'Performance' },
    { id: '11', label: 'FidelityFX Super Resolution', value: 'Disabled (Highest Quality)' },
    { id: '12', label: 'NVIDIA Reflex Low Latency', value: 'Disabled' },
  ]);

  const [rewards, setRewards] = useState<Reward[]>([
    { id: '1', title: 'Ongoing Giveaway', imageUrl: '/giveaway-image.jpg', link: '#' }
  ]);

  const [sponsors, setSponsors] = useState<Sponsor[]>([
    { id: '1', label: 'Sponsor 1', link: 'https://example.com' }
  ]);

  const getIconComponent = (iconName: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      faceit: <img src="/fafaceit.webp" alt="Faceit" className="w-6 h-6 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />,
      steam: <FaSteam />,
      instagram: <FaInstagram />,
      discord: <FaDiscord />,
      twitter: <FaTwitter />,
      twitch: <FaTwitch />,
      youtube: <FaYoutube />,
      tiktok: <FaTiktok />,
    };
    return iconMap[iconName] || <FaSteam />;
  };

  const loadContent = async () => {
    if (!sessionId) return;
    try {
      const response = await fetch('/api/admin/content', {
        headers: { 'x-session-id': sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setSocialLinks(data.socialLinks || socialLinks);
        setSystemSpecs(data.systemSpecs || systemSpecs);
        setCs2Settings(data.cs2Settings || cs2Settings);
        setViewmodel(data.viewmodel || viewmodel);
        setVideoSettings(data.videoSettings || videoSettings);
        setBinds(data.binds || binds);
        setFaqs(data.faqs || faqs);
        setConfigSection(data.configSection || configSection);
        setMouseSettings(data.mouseSettings || mouseSettings);
        setAdvancedVideoSettings(data.advancedVideoSettings || advancedVideoSettings);
        setRewards(data.rewards || rewards);
        setSponsors(data.sponsors || sponsors);
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setIsInitialized(true);
    }
  };

  const handleLogin = (newSessionId: string) => { login(newSessionId); };

  const handleLogout = async () => {
    if (sessionId) {
      try {
        await fetch('/api/admin/logout', {
          method: 'POST',
          headers: { 'x-session-id': sessionId }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    logout();
    setIsInitialized(false);
  };

  const handleSave = async () => {
    if (!sessionId) return;
    setIsLoading(true);
    setMessage('');
    try {
      const response = await fetch('/api/admin/content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-session-id': sessionId },
        body: JSON.stringify({
          socialLinks, systemSpecs, cs2Settings, viewmodel, videoSettings,
          binds, faqs, configSection, mouseSettings, advancedVideoSettings, rewards, sponsors,
        }),
      });
      if (response.ok) {
        triggerRefresh();
        setMessage('Content saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else if (response.status === 401) {
        handleLogout();
        setMessage('Session expired. Please login again.');
      } else {
        setMessage('Failed to save content');
      }
    } catch (error) {
      setMessage('Error saving content');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addItem = (type: string) => {
    const newId = Date.now().toString();
    switch (type) {
      case 'social': setSocialLinks([...socialLinks, { id: newId, label: 'New Social', link: '', icon: 'steam' }]); break;
      case 'system': setSystemSpecs([...systemSpecs, { id: newId, label: 'New Spec', value: '' }]); break;
      case 'cs2': setCs2Settings([...cs2Settings, { id: newId, label: 'New Setting', value: '' }]); break;
      case 'viewmodel': setViewmodel([...viewmodel, { id: newId, label: 'New Setting', value: '' }]); break;
      case 'video': setVideoSettings([...videoSettings, { id: newId, label: 'New Setting', value: '' }]); break;
      case 'mouse': setMouseSettings([...mouseSettings, { id: newId, label: 'New Setting', value: '' }]); break;
      case 'advanced': setAdvancedVideoSettings([...advancedVideoSettings, { id: newId, label: 'New Setting', value: '', status: undefined }]); break;
      case 'bind': setBinds([...binds, { id: newId, label: 'New Bind', key: '' }]); break;
      case 'faq': setFaqs([...faqs, { id: newId, question: 'New Question', answer: 'New Answer' }]); break;
      case 'reward': setRewards([...rewards, { id: newId, title: 'New Reward', imageUrl: '', link: '#' }]); break;
      case 'sponsor': setSponsors([...sponsors, { id: newId, label: 'New Sponsor', link: '#' }]); break;
    }
  };

  const deleteItem = (type: string, id: string) => {
    switch (type) {
      case 'social': setSocialLinks(socialLinks.filter(item => item.id !== id)); break;
      case 'system': setSystemSpecs(systemSpecs.filter(item => item.id !== id)); break;
      case 'cs2': setCs2Settings(cs2Settings.filter(item => item.id !== id)); break;
      case 'viewmodel': setViewmodel(viewmodel.filter(item => item.id !== id)); break;
      case 'video': setVideoSettings(videoSettings.filter(item => item.id !== id)); break;
      case 'mouse': setMouseSettings(mouseSettings.filter(item => item.id !== id)); break;
      case 'advanced': setAdvancedVideoSettings(advancedVideoSettings.filter(item => item.id !== id)); break;
      case 'bind': setBinds(binds.filter(item => item.id !== id)); break;
      case 'faq': setFaqs(faqs.filter(item => item.id !== id)); break;
      case 'reward': setRewards(rewards.filter(item => item.id !== id)); break;
      case 'sponsor': setSponsors(sponsors.filter(item => item.id !== id)); break;
    }
  };

  const updateItem = (type: string, id: string, field: string, value: string) => {
    switch (type) {
      case 'social': setSocialLinks(socialLinks.map(item => item.id === id ? { ...item, [field]: value } : item)); break;
      case 'system': setSystemSpecs(systemSpecs.map(item => item.id === id ? { ...item, [field]: value } : item)); break;
      case 'cs2': setCs2Settings(cs2Settings.map(item => item.id === id ? { ...item, [field]: value } : item)); break;
      case 'viewmodel': setViewmodel(viewmodel.map(item => item.id === id ? { ...item, [field]: value } : item)); break;
      case 'video': setVideoSettings(videoSettings.map(item => item.id === id ? { ...item, [field]: value } : item)); break;
      case 'mouse': setMouseSettings(mouseSettings.map(item => item.id === id ? { ...item, [field]: value } : item)); break;
      case 'advanced': setAdvancedVideoSettings(advancedVideoSettings.map(item => item.id === id ? { ...item, [field]: value } : item)); break;
      case 'bind': setBinds(binds.map(item => item.id === id ? { ...item, [field]: value } : item)); break;
      case 'faq': setFaqs(faqs.map(item => item.id === id ? { ...item, [field]: value } : item)); break;
      case 'reward': setRewards(rewards.map(item => item.id === id ? { ...item, [field]: value } : item)); break;
      case 'sponsor': setSponsors(sponsors.map(item => item.id === id ? { ...item, [field]: value } : item)); break;
    }
  };

  useEffect(() => {
    if (isAuthenticated && sessionId) { loadContent(); }
  }, [isAuthenticated, sessionId]);

  if (!isAuthenticated) return <LoginPage onLogin={handleLogin} />;

  if (!isInitialized) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-white text-xl">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full">
      <div className="max-w-7xl mx-auto py-6 sm:py-10 px-3 sm:px-4">

        {/* CHANGED: Header stacks on mobile, row on sm+ */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-white tracking-wider">Admin Panel</h1>
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-colors disabled:opacity-50 text-sm sm:text-base"
            >
              <FaSave />
              <span>{isLoading ? 'Saving...' : 'Save All Changes'}</span>
            </button>
            <button
              onClick={handleLogout}
              className="bg-gray-600 hover:bg-gray-700 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-semibold flex items-center space-x-2 transition-colors text-sm sm:text-base"
            >
              <FaSignOutAlt />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.includes('success') ? 'bg-green-600' : 'bg-red-600'} text-white text-sm sm:text-base`}>
            {message}
          </div>
        )}

        {/* CHANGED: Tabs scroll horizontally on mobile, wrap naturally on desktop */}
        <div className="flex mb-6 sm:mb-8 bg-gray-800 p-1 rounded-lg overflow-x-auto gap-1 scrollbar-none">
          {[
            { id: 'social', label: 'Social' },
            { id: 'system', label: 'System' },
            { id: 'cs2', label: 'CS2' },
            { id: 'viewmodel', label: 'Viewmodel' },
            { id: 'video', label: 'Video' },
            { id: 'config', label: 'Config' },
            { id: 'mouse', label: 'Mouse' },
            { id: 'advanced', label: 'Advanced' },
            { id: 'binds', label: 'Binds' },
            { id: 'faq', label: 'FAQs' },
            { id: 'rewards', label: 'Rewards' },
            { id: 'sponsors', label: 'Sponsors' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 rounded-md font-medium transition-colors whitespace-nowrap text-xs sm:text-sm ${
                activeTab === tab.id
                  ? 'bg-red-600 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-gray-800 rounded-xl p-3 sm:p-6">

          {/* Social Media Tab */}
          {activeTab === 'social' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-white">Social Media Links</h2>
                <button onClick={() => addItem('social')} className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 text-sm">
                  <FaPlus /><span>Add</span>
                </button>
              </div>
              <div className="space-y-3">
                {socialLinks.map((link) => (
                  // CHANGED: stacks vertically on mobile
                  <div key={link.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 sm:p-4 bg-gray-700 rounded-lg">
                    <div className="text-white text-xl flex-shrink-0">{getIconComponent(link.icon)}</div>
                    <input type="text" value={link.label} onChange={(e) => updateItem('social', link.id, 'label', e.target.value)} className="w-full sm:flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Label" />
                    <input type="text" value={link.link} onChange={(e) => updateItem('social', link.id, 'link', e.target.value)} className="w-full sm:flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="URL" />
                    <div className="flex items-center gap-2">
                      <select value={link.icon} onChange={(e) => updateItem('social', link.id, 'icon', e.target.value)} className="flex-1 sm:flex-none bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm">
                        <option value="faceit">Faceit</option>
                        <option value="steam">Steam</option>
                        <option value="instagram">Instagram</option>
                        <option value="discord">Discord</option>
                        <option value="twitter">Twitter</option>
                        <option value="twitch">Twitch</option>
                        <option value="youtube">Youtube</option>
                        <option value="tiktok">Tiktok</option>
                      </select>
                      <button onClick={() => deleteItem('social', link.id)} className="text-red-400 hover:text-red-300 flex-shrink-0"><FaTrash /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* System Specs Tab */}
          {activeTab === 'system' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-white">System Specifications</h2>
                <button onClick={() => addItem('system')} className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 text-sm">
                  <FaPlus /><span>Add</span>
                </button>
              </div>
              <div className="space-y-3">
                {systemSpecs.map((spec) => (
                  <div key={spec.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 sm:p-4 bg-gray-700 rounded-lg">
                    <input type="text" value={spec.label} onChange={(e) => updateItem('system', spec.id, 'label', e.target.value)} className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Label" />
                    <input type="text" value={spec.value} onChange={(e) => updateItem('system', spec.id, 'value', e.target.value)} className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Value" />
                    <button onClick={() => deleteItem('system', spec.id)} className="text-red-400 hover:text-red-300 flex-shrink-0 self-end sm:self-auto"><FaTrash /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CS2 Settings Tab */}
          {activeTab === 'cs2' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-white">CS2 Settings</h2>
                <button onClick={() => addItem('cs2')} className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 text-sm">
                  <FaPlus /><span>Add</span>
                </button>
              </div>
              <div className="space-y-3">
                {cs2Settings.map((setting) => (
                  <div key={setting.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 sm:p-4 bg-gray-700 rounded-lg">
                    <input type="text" value={setting.label} onChange={(e) => updateItem('cs2', setting.id, 'label', e.target.value)} className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Label" />
                    <input type="text" value={setting.value} onChange={(e) => updateItem('cs2', setting.id, 'value', e.target.value)} className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Value" />
                    <button onClick={() => deleteItem('cs2', setting.id)} className="text-red-400 hover:text-red-300 flex-shrink-0 self-end sm:self-auto"><FaTrash /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Viewmodel Tab */}
          {activeTab === 'viewmodel' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-white">Viewmodel Settings</h2>
                <button onClick={() => addItem('viewmodel')} className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 text-sm">
                  <FaPlus /><span>Add</span>
                </button>
              </div>
              <div className="space-y-3">
                {viewmodel.map((setting) => (
                  <div key={setting.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 sm:p-4 bg-gray-700 rounded-lg">
                    <input type="text" value={setting.label} onChange={(e) => updateItem('viewmodel', setting.id, 'label', e.target.value)} className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Label" />
                    <input type="text" value={setting.value} onChange={(e) => updateItem('viewmodel', setting.id, 'value', e.target.value)} className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Value" />
                    <button onClick={() => deleteItem('viewmodel', setting.id)} className="text-red-400 hover:text-red-300 flex-shrink-0 self-end sm:self-auto"><FaTrash /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Video Settings Tab */}
          {activeTab === 'video' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-white">Video Settings</h2>
                <button onClick={() => addItem('video')} className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 text-sm">
                  <FaPlus /><span>Add</span>
                </button>
              </div>
              <div className="space-y-3">
                {videoSettings.map((setting) => (
                  <div key={setting.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 sm:p-4 bg-gray-700 rounded-lg">
                    <input type="text" value={setting.label} onChange={(e) => updateItem('video', setting.id, 'label', e.target.value)} className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Label" />
                    <input type="text" value={setting.value} onChange={(e) => updateItem('video', setting.id, 'value', e.target.value)} className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Value" />
                    <button onClick={() => deleteItem('video', setting.id)} className="text-red-400 hover:text-red-300 flex-shrink-0 self-end sm:self-auto"><FaTrash /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Binds Tab */}
          {activeTab === 'binds' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-white">Key Binds</h2>
                <button onClick={() => addItem('bind')} className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 text-sm">
                  <FaPlus /><span>Add</span>
                </button>
              </div>
              {/* CHANGED: 1 col mobile, 2 col md, 3 col lg */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {binds.map((bind) => (
                  <div key={bind.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 sm:p-4 bg-gray-700 rounded-lg">
                    <div className="flex-1">
                      <input type="text" value={bind.label} onChange={(e) => updateItem('bind', bind.id, 'label', e.target.value)} className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 mb-2 text-sm" placeholder="Action" />
                      <input type="text" value={bind.key} onChange={(e) => updateItem('bind', bind.id, 'key', e.target.value)} className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Key" />
                    </div>
                    <button onClick={() => deleteItem('bind', bind.id)} className="text-red-400 hover:text-red-300 flex-shrink-0"><FaTrash /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Config Tab */}
          {activeTab === 'config' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-white">Config Section</h2>
              </div>
              <div className="space-y-3 sm:space-y-4">
                {[
                  { label: 'Config Link', key: 'configLink', multiline: false, placeholder: 'Config download link' },
                  { label: 'Launch Options', key: 'launchOptions', multiline: false, placeholder: 'Launch options text' },
                  { label: 'Crosshair', key: 'crosshair', multiline: false, placeholder: 'Crosshair code' },
                  { label: 'Viewmodel', key: 'viewmodel', multiline: true, placeholder: 'Viewmodel commands' },
                  { label: 'HUD', key: 'hud', multiline: true, placeholder: 'HUD commands' },
                  { label: 'Radar', key: 'radar', multiline: true, placeholder: 'Radar commands' },
                ].map(({ label, key, multiline, placeholder }) => (
                  <div key={key} className="p-3 sm:p-4 bg-gray-700 rounded-lg">
                    <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
                    {multiline ? (
                      <textarea value={(configSection as any)[key]} onChange={(e) => setConfigSection({ ...configSection, [key]: e.target.value })} className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 h-20 resize-none text-sm" placeholder={placeholder} />
                    ) : (
                      <input type="text" value={(configSection as any)[key]} onChange={(e) => setConfigSection({ ...configSection, [key]: e.target.value })} className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder={placeholder} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mouse Settings Tab */}
          {activeTab === 'mouse' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-white">Mouse Settings</h2>
                <button onClick={() => addItem('mouse')} className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 text-sm">
                  <FaPlus /><span>Add</span>
                </button>
              </div>
              <div className="space-y-3">
                {mouseSettings.map((setting) => (
                  <div key={setting.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 sm:p-4 bg-gray-700 rounded-lg">
                    <input type="text" value={setting.label} onChange={(e) => updateItem('mouse', setting.id, 'label', e.target.value)} className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Label" />
                    <input type="text" value={setting.value} onChange={(e) => updateItem('mouse', setting.id, 'value', e.target.value)} className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Value" />
                    <button onClick={() => deleteItem('mouse', setting.id)} className="text-red-400 hover:text-red-300 flex-shrink-0 self-end sm:self-auto"><FaTrash /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Advanced Video Settings Tab */}
          {activeTab === 'advanced' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-white">Advanced Video Settings</h2>
                <button onClick={() => addItem('advanced')} className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 text-sm">
                  <FaPlus /><span>Add</span>
                </button>
              </div>
              <div className="space-y-3">
                {advancedVideoSettings.map((setting) => (
                  <div key={setting.id} className="p-3 sm:p-4 bg-gray-700 rounded-lg">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 space-y-2 sm:space-y-4">
                        <input type="text" value={setting.label} onChange={(e) => updateItem('advanced', setting.id, 'label', e.target.value)} className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Setting name" />
                        <input type="text" value={setting.value} onChange={(e) => updateItem('advanced', setting.id, 'value', e.target.value)} className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Setting value" />
                        <select value={setting.status || ''} onChange={(e) => updateItem('advanced', setting.id, 'status', e.target.value)} className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm">
                          <option value="">No status</option>
                          <option value="enabled">Enabled</option>
                          <option value="disabled">Disabled</option>
                        </select>
                      </div>
                      <button onClick={() => deleteItem('advanced', setting.id)} className="text-red-400 hover:text-red-300 flex-shrink-0 mt-1"><FaTrash /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQ Tab */}
          {activeTab === 'faq' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-white">Frequently Asked Questions</h2>
                <button onClick={() => addItem('faq')} className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 text-sm">
                  <FaPlus /><span>Add</span>
                </button>
              </div>
              <div className="space-y-3">
                {faqs.map((faq) => (
                  <div key={faq.id} className="p-3 sm:p-4 bg-gray-700 rounded-lg">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 space-y-2 sm:space-y-4">
                        <input type="text" value={faq.question} onChange={(e) => updateItem('faq', faq.id, 'question', e.target.value)} className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Question" />
                        <textarea value={faq.answer} onChange={(e) => updateItem('faq', faq.id, 'answer', e.target.value)} className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 h-24 resize-none text-sm" placeholder="Answer" />
                      </div>
                      <button onClick={() => deleteItem('faq', faq.id)} className="text-red-400 hover:text-red-300 flex-shrink-0 mt-1"><FaTrash /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rewards Tab */}
          {activeTab === 'rewards' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-white">Rewards</h2>
                <button onClick={() => addItem('reward')} className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 text-sm">
                  <FaPlus /><span>Add</span>
                </button>
              </div>
              <div className="space-y-3">
                {rewards.map((reward) => (
                  <div key={reward.id} className="p-3 sm:p-4 bg-gray-700 rounded-lg">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 space-y-3">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">Title</label>
                          <input type="text" value={reward.title} onChange={(e) => updateItem('reward', reward.id, 'title', e.target.value)} className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Reward title" />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">Image URL</label>
                          <input type="text" value={reward.imageUrl} onChange={(e) => updateItem('reward', reward.id, 'imageUrl', e.target.value)} className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="/path/to/image.jpg" />
                          {reward.imageUrl && (
                            <div className="mt-2">
                              <img src={reward.imageUrl} alt={reward.title} className="max-w-[160px] sm:max-w-xs h-auto rounded border border-gray-600" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">Link URL</label>
                          <input type="text" value={reward.link} onChange={(e) => updateItem('reward', reward.id, 'link', e.target.value)} className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="https://example.com or #" />
                        </div>
                      </div>
                      <button onClick={() => deleteItem('reward', reward.id)} className="text-red-400 hover:text-red-300 flex-shrink-0 mt-1"><FaTrash /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sponsors Tab */}
          {activeTab === 'sponsors' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-white">Sponsors</h2>
                <button onClick={() => addItem('sponsor')} className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 text-sm">
                  <FaPlus /><span>Add</span>
                </button>
              </div>
              <div className="space-y-3">
                {sponsors.map((sponsor) => (
                  <div key={sponsor.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 sm:p-4 bg-gray-700 rounded-lg">
                    <input type="text" value={sponsor.label} onChange={(e) => updateItem('sponsor', sponsor.id, 'label', e.target.value)} className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="Sponsor name" />
                    <input type="text" value={sponsor.link} onChange={(e) => updateItem('sponsor', sponsor.id, 'link', e.target.value)} className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 text-sm" placeholder="URL" />
                    <button onClick={() => deleteItem('sponsor', sponsor.id)} className="text-red-400 hover:text-red-300 flex-shrink-0 self-end sm:self-auto"><FaTrash /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AdminPage;